/**
 * Emergency calendar local-view reset.
 *
 * Clears zoom / scroll / month cache keys for MAX + EXTREME calendars and reloads.
 * Does NOT touch auth tokens, dark mode, or unrelated app data.
 *
 * Hotkey: Ctrl + Shift + Alt + R
 */

export const CALENDAR_VIEW_RESET_STORAGE_KEYS = [
   "__DV_EXTREME_ZOOM_PCT",
   "__DV_EXTREME_SCROLL_STATE_V1",
   "__DV_EXTREME_MONTH",
   "__DV_MAX_ZOOM_PCT",
   "__DV_MAX_SCROLL_STATE_V1",
   "__DV_MAX_MONTH",
];

export const CALENDAR_VIEW_RESET_HOTKEY = {
   ctrl: true,
   shift: true,
   alt: true,
   key: "r",
   label: "Ctrl+Shift+Alt+R",
};

function safeRemoveItem(storage, key) {
   try {
      if (!storage) return false;
      if (storage.getItem(key) == null) return false;
      storage.removeItem(key);
      return true;
   } catch {
      return false;
   }
}

/**
 * @returns {{ removed: string[], ok: boolean }}
 */
export function resetCalendarLocalViewState({
   clearSession = true,
   reload = false,
} = {}) {
   const removed = [];
   if (typeof window === "undefined") {
      return { removed, ok: false };
   }

   for (const key of CALENDAR_VIEW_RESET_STORAGE_KEYS) {
      if (safeRemoveItem(window.localStorage, key)) {
         removed.push(`localStorage:${key}`);
      }
      if (clearSession && safeRemoveItem(window.sessionStorage, key)) {
         removed.push(`sessionStorage:${key}`);
      }
   }

   // eslint-disable-next-line no-console
   console.info(
      `[calendar-view-reset] Cleared ${removed.length} key(s)`,
      removed,
   );

   if (reload) {
      try {
         window.location.reload();
      } catch {}
   }

   return { removed, ok: true };
}

function matchesResetHotkey(event) {
   if (!event) return false;
   if (!(event.ctrlKey || event.metaKey)) return false;
   if (!event.shiftKey) return false;
   if (!event.altKey) return false;
   const key = String(event.key || "").toLowerCase();
   return key === CALENDAR_VIEW_RESET_HOTKEY.key;
}

export function bootstrapCalendarViewResetHotkey() {
   if (typeof window === "undefined") return;
   if (window.__calendarViewResetHotkeyBound) return;
   window.__calendarViewResetHotkeyBound = true;

   window.__calendarViewReset = {
      help() {
         // eslint-disable-next-line no-console
         console.log(`
Calendar view reset
-------------------
Hotkey: ${CALENDAR_VIEW_RESET_HOTKEY.label}
Clears: zoom, scroll position, saved month for MAX + EXTREME
Then reloads the page.

API:
  __calendarViewReset.run()
  __calendarViewReset.keys
`);
      },
      keys: CALENDAR_VIEW_RESET_STORAGE_KEYS,
      run(options = {}) {
         return resetCalendarLocalViewState({
            clearSession: true,
            reload: true,
            ...options,
         });
      },
   };

   window.addEventListener(
      "keydown",
      (event) => {
         if (!matchesResetHotkey(event)) return;
         // Avoid firing inside inputs / editors.
         const target = event.target;
         const tag = String(target?.tagName || "").toLowerCase();
         if (
            tag === "input" ||
            tag === "textarea" ||
            tag === "select" ||
            target?.isContentEditable
         ) {
            return;
         }

         event.preventDefault();
         event.stopPropagation();

         const confirmed =
            typeof window.confirm === "function"
               ? window.confirm(
                    "Resetează vizualizarea calendarului?\n\n" +
                       "Șterge zoom-ul, scroll-ul salvat și luna memorată (MAX + EXTREME),\n" +
                       "apoi reîncarcă pagina.\n\n" +
                       `Scurtătură: ${CALENDAR_VIEW_RESET_HOTKEY.label}`,
                 )
               : true;

         if (!confirmed) return;
         resetCalendarLocalViewState({ clearSession: true, reload: true });
      },
      true,
   );

   // eslint-disable-next-line no-console
   console.info(
      `[calendar-view-reset] Hotkey ready: ${CALENDAR_VIEW_RESET_HOTKEY.label}`,
   );
}
